import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, ShoppingCart, Package, RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ServicePage = () => {
  const { serviceId } = useParams();
  const { t, language } = useLanguage();
  const { addItem } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [service, setService] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch service details
      const serviceRes = await fetch(`${API_URL}/api/service-categories`);
      if (serviceRes.ok) {
        const categories = await serviceRes.json();
        const found = categories.find(c => c.category_id === serviceId);
        setService(found);
      }

      // Fetch products for this service
      const productsRes = await fetch(`${API_URL}/api/services/${serviceId}/products`);
      if (productsRes.ok) {
        const data = await productsRes.json();
        setProducts(data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  }, [serviceId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddToCart = (product) => {
    if (!user) {
      toast.error(
        language === 'pt' 
          ? 'Faça login para adicionar ao carrinho'
          : 'Please login to add to cart'
      );
      navigate(`/login?redirect=/service/${serviceId}`);
      return;
    }

    addItem({
      item_id: product.product_id,
      name: product.name,
      price: product.price,
      service_id: serviceId,
      service_name: service?.name || 'Service'
    });
    
    toast.success(
      language === 'pt' 
        ? `${product.name} adicionado ao carrinho`
        : `${product.name} added to cart`
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] py-8">
        <div className="container mx-auto px-4">
          <Skeleton className="h-12 w-64 mb-4" />
          <Skeleton className="h-6 w-96 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array(6).fill(0).map((_, i) => (
              <Skeleton key={i} className="h-48 rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#004E64] mb-4">
            {language === 'pt' ? 'Serviço não encontrado' : 'Service not found'}
          </h2>
          <Link to="/">
            <Button className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full">
              {language === 'pt' ? 'Voltar ao Início' : 'Back to Home'}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <div 
        className="h-48 bg-gradient-to-r from-[#004E64] to-[#007991] relative"
        style={service.image ? { backgroundImage: `url(${service.image})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
      >
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="container mx-auto px-4 h-full flex items-end pb-6 relative z-10">
          <div>
            <Link to="/" className="text-white/80 hover:text-white flex items-center gap-2 mb-4">
              <ArrowLeft size={20} />
              {language === 'pt' ? 'Voltar' : 'Back'}
            </Link>
            <h1 className="text-3xl md:text-4xl font-bold text-white">
              {language === 'pt' ? service.name_pt : service.name}
            </h1>
            <p className="text-white/80 mt-2">
              {language === 'pt' ? service.description_pt : service.description}
            </p>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-[#004E64]">
            {language === 'pt' ? 'Produtos Disponíveis' : 'Available Products'}
          </h2>
          <Button variant="outline" size="sm" onClick={fetchData} className="rounded-full">
            <RefreshCw size={16} className="mr-1" />
            {language === 'pt' ? 'Atualizar' : 'Refresh'}
          </Button>
        </div>

        {products.length === 0 ? (
          <div className="text-center py-16">
            <Package size={64} className="mx-auto text-[#9FA2B4] mb-4" />
            <h3 className="text-xl font-semibold text-[#004E64] mb-2">
              {language === 'pt' ? 'Nenhum produto disponível' : 'No products available'}
            </h3>
            <p className="text-[#9FA2B4]">
              {language === 'pt' 
                ? 'Este serviço ainda não tem produtos cadastrados.'
                : 'This service has no products registered yet.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map(product => (
              <Card key={product.product_id} className="rounded-2xl overflow-hidden hover:shadow-lg transition-shadow">
                {product.image && (
                  <div className="h-40 overflow-hidden">
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-[#004E64]">{product.name}</h3>
                    {product.is_popular && (
                      <Badge className="bg-[#FF6B35]/10 text-[#FF6B35]">Popular</Badge>
                    )}
                  </div>
                  <p className="text-sm text-[#9FA2B4] mb-4 line-clamp-2">
                    {product.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xl font-bold text-[#FF6B35]">
                      €{product.price.toFixed(2)}
                    </span>
                    {user ? (
                      <Button
                        size="sm"
                        className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full"
                        onClick={() => handleAddToCart(product)}
                        disabled={!product.is_available}
                      >
                        <Plus size={18} className="mr-1" />
                        {product.is_available 
                          ? (language === 'pt' ? 'Adicionar' : 'Add')
                          : (language === 'pt' ? 'Indisponível' : 'Unavailable')}
                      </Button>
                    ) : (
                      <Link to={`/login?redirect=/service/${serviceId}`}>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full border-[#FF6B35] text-[#FF6B35]"
                        >
                          {language === 'pt' ? 'Entrar' : 'Login'}
                        </Button>
                      </Link>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ServicePage;
